import config from 'config';
import { AdLogo } from 'core/adLogo/AdLogo';
import { LimitationData } from 'core/limitation/Limitation';

/**
 * 戶外素材裝的是什麼媒材。
 *
 * ⚠️ 這些數值是**前後端共用的儲存值**（對照後端
 * `rtbplan/src/main/java/io/tenmax/pmax/rtbplan/addata/OutdoorType.java`），所以每個成員
 * 一律顯式賦值，不要靠隱式遞增。ECOPD-5681 移除中間的 `DYNAMIC_VIDEO = 5` 時就踩到這點：
 * 隱式編號下刪掉它，`IMAGE` 會靜默從 6 掉到 5，線上電子紙素材全被讀成另一個型別，
 * 而 TypeScript 一個錯都不會報。5 從此空著不再使用。
 */
export enum OutdoorType {
  VIDEO = 1,
  IMAGE_AUDIO = 2,
  VIDEO_IMAGE = 3,
  HTML5 = 4,
  // ECOPD-5617: 純圖片。電子紙看板沒有喇叭，沿用 IMAGE_AUDIO 只能靠顯示層改字
  // （getOutdoorTypeLabel 的 outdoorType2Epaper），資料上仍是「圖 + 音」，銷售渠道的
  // 「支援的戶外廣告類型」因此講不清楚 —— 建立電子紙廣告活動類型時就卡在這裡。
  IMAGE = 6
}

/**
 * 這個戶外類型走不走「圖 + 音」那條分支。
 *
 * IMAGE（純圖片）與 IMAGE_AUDIO 的差別只在銷售渠道講不講得清楚，素材的資料形狀兩者
 * 相同（見上方 enum 的註解與 BE OutdoorType 的 javadoc）⇒ 下游一律走同一條分支，
 * 不為 IMAGE 另開資料路徑。集中成一個判別式是因為這條規則散落在表單欄位、媒體收集、
 * 尺寸推導、列表格式、預覽與詳細頁 —— 漏掉任何一處都不會編譯錯，只會靜默少做事
 * （ECOPD-5641）。
 */
export const isImageAudioFamily = (outdoorType?: OutdoorType): boolean =>
  outdoorType === OutdoorType.IMAGE_AUDIO || outdoorType === OutdoorType.IMAGE;

export type CreativeApprovalStatus = {
  [adxName: string]: {
    state: number;
    attr: number[];
    udt: string;
  };
};

export type CreativeSize = {
  width: number;
  height: number;
};

export type NativeBanner = {
  creativeId: number;
  creativeReview: CreativeApprovalStatus;
  width: number;
  height: number;
  htmlUrl: string;
};

export type NativeBannerMap = { [key: `${number}x${number}`]: NativeBanner };

export type CreativeOfCampaign = {
  rtbCreativeId: any;
  id: number;
  name: string;
  status: number;
  vtr: number;
  vctr: number;
  vimps: number;
  view: number;
  ctr: number;
  imps: number;
  clicks: number;
  size: string;
  creativeType: number;
  outdoorType?: OutdoorType;
  enableStartTime?: string;
  enableEndTime?: string;
  approvalStatus: CreativeApprovalStatus;
  bannerImageUrl: string;
  creativeValue: any;
  createTime: string;
  bannerUrl: string;
  landingPageUrl: string;
  isActiveBinding: boolean;
  tenmaxCategory: string;
  origTenmaxCategory: string | null;
  shortcutUrl: string;
  bindingState: CampaignBannerMapState;
  nativeBanner?: NativeBannerMap;
  effectiveStatus?: string;
  issuesInfo?: string;
  adReviewFeedback?: string;
  l3ChannelId: string;
  report?: any;
  bannerExtra?: { [key: string]: string | number | boolean | object };
  previewLink?: string;
  ppsLayoutId?: string;
  subLayout?: string;
  videoChildren?: VideoChildMap;
};

export type Creative = {
  basic: CreativeBasic;
  limitations: LimitationData;
};

export type VideoChildMap = {
  [deviceType: string]: {
    creativeId: number;
    videoUrl?: string;
    approvalStatus: CreativeApprovalStatus;
  };
};

export type CreativeBasic = {
  advertiserId: number;
  agencyId: number;
  bannerUrl: string;
  creativeId: number;
  creativeType: CreativeType;
  creativeValues: any;
  landingPageUrl: string;
  name: string;
  tenmaxCategory: string;
  bannerExtra?: { [key: string]: string | number | boolean | object };
  enableNativeBanner?: boolean;
  nativeBanner?: NativeBannerMap;
  bannerImageUrl?: string;
  outdoorType?: OutdoorType;
  duration?: number;
  enableStartTime?: string;
  enableEndTime?: string;
  ppsLayoutId?: string;
  subLayout?: string;
  adLogo?: AdLogo;
};

export type BindingCampaign = {
  id: number;
  name: string;
  orderNumber: string;
  active: boolean;
  isActive: boolean;
  canUnbind: boolean;
};

export enum CampaignBannerMapState {
  DEFAULT,
  ENABLE,
  DISABLE,
  WAITING_FB_UPDATE,
  BEFORE_START_TIME,
  AFTER_END_TIME,
  IN_PROGRESS
}

export type CreativeListRecord = {
  advertiserId: number;
  approvalStatus: CreativeApprovalStatus;
  bannerImageUrl: string | null;
  bannerUrl: string;
  bindingCampaign: BindingCampaign[];
  bindingCount: { enable: number; disable: number };
  createTime: string;
  creativeId: number;
  creativeState: CreativeStatus;
  creativeType: CreativeType;
  creativeValues: { [key: string]: string | number | boolean | object };
  limitations: LimitationData;
  name: string;
  tenmaxCategory: string;
  origTenmaxCategory: string | null;
  bannerExtra?: { [key: string]: string | number | boolean | object };
  enableNativeBanner: boolean;
  nativeBanner?: NativeBannerMap;
  ppsLayoutId?: string;
  outdoorType?: OutdoorType;
  duration?: number;
  enableStartTime?: string;
  enableEndTime?: string;
  subLayout?: string;
  videoChildren?: VideoChildMap;
};

export enum VideoPlacement {
  IN_STREAM = 1,
  OUT_STREAM,
  UNLIMIT
}

export enum CreativeType {
  IMAGE = 1,
  HTML5,
  NATIVE,
  VIDEO,
  // DYNAMIC,
  THIRD_PARTY = 8,
  COMBO = 10,
  EDIMAX,
  ONE_FOR_ALL,
  ONE_FOR_ALL_DISPLAY,
  ONE_FOR_ALL_DISPLAY_IOS,
  ONE_FOR_ALL_DISPLAY_ANDROID,
  ONE_FOR_ALL_VIDEO,
  ONE_FOR_ALL_VIDEO_IOS,
  ONE_FOR_ALL_VIDEO_ANDROID,
  CTV = 23,
  CUSTOM_RECTANGLE,
  CUSTOM_BOTTOM,
  PIC_SHORT,
  PIC_LONG,
  SANDBOX_OUTDOOR = 35,
  FRIDGE_LONG = 37,
  PIC_EXTENDED,
  FRIDGE_EXTENDED,
  PIC_BROAD,
  OUTDOOR_LINE_MESSAGE,
  FRIDGE_BROAD,
  LINE_MESSAGE,
  HWACOM_SHORT,
  HWACOM_LONG,
  HWACOM_EXTENDED,
  HWACOM_BROAD
  // ECOPD-5681：48 原為冰櫃的動態影片素材（DVC），隨天氣素材服務一起下架，號碼不再使用。
}

export const videoNeedsProcessing = (
  creativeType: number,
  outdoorType?: OutdoorType
) => {
  if (CreativeType.COMBO === creativeType) {
    return true;
  }
  if (
    [
      CreativeType.FRIDGE_LONG,
      CreativeType.FRIDGE_EXTENDED,
      CreativeType.FRIDGE_BROAD
    ].includes(creativeType) &&
    outdoorType === OutdoorType.VIDEO
  ) {
    return true;
  }
  return false;
};

export const isNative = (creativeType: number) => {
  return CreativeType.NATIVE === creativeType;
};

export const isCombo = (creativeType: number) => {
  return CreativeType.COMBO === creativeType;
};

export const isImage = (creativeType: number) => {
  return CreativeType.IMAGE === creativeType;
};

export const isVideo = (creativeType: number) => {
  return CreativeType.VIDEO === creativeType;
};

export const isOutdoor = (creativeType: number) => {
  return [
    CreativeType.EDIMAX,
    CreativeType.PIC_SHORT,
    CreativeType.PIC_LONG,
    CreativeType.PIC_EXTENDED,
    CreativeType.PIC_BROAD,
    CreativeType.SANDBOX_OUTDOOR,
    CreativeType.FRIDGE_LONG,
    CreativeType.FRIDGE_EXTENDED,
    CreativeType.FRIDGE_BROAD,
    CreativeType.OUTDOOR_LINE_MESSAGE,
    CreativeType.LINE_MESSAGE,
    CreativeType.HWACOM_SHORT,
    CreativeType.HWACOM_LONG,
    CreativeType.HWACOM_EXTENDED,
    CreativeType.HWACOM_BROAD
  ].includes(creativeType);
};

export const isThirdParty = (creativeType: number) => {
  return CreativeType.THIRD_PARTY === creativeType;
};

/**
 * 判斷是否為已下架、只能唯讀的 One For All 素材類型。
 * @param creativeType 素材類型
 * @returns 是 One For All 系列（含 iOS／Android 變體）時為 true
 */
export const isOneForAllCreativeType = (creativeType: number) =>
  creativeType >= CreativeType.ONE_FOR_ALL &&
  creativeType <= CreativeType.ONE_FOR_ALL_VIDEO_ANDROID;

export type CreativeValue = {
  w?: number;
  h?: number;
  imageUrl?: number;
  title?: string;
  desc?: string;
  img?: ImageData;
  img_l?: ImageData;
  img_s?: ImageData;
  icon?: ImageData;
  icon_l?: ImageData;
  icon_s?: ImageData;
  sponsored?: Sponsored;
};

export type ImageData = {
  url: string;
  w: number;
  h: number;
};

export type Sponsored = {
  name: string;
  link: string;
};

export enum CreativeStatus {
  'NO_BINDING' = 0,
  'ACTIVE' = 1,
  'DEACTIVE',
  'PROCESSING' = 5,
  'PROCESSING_FAIL',
  'ARCHIVED'
}

export enum ServerState {
  ACTIVATE = 1,
  DEACTIVATE,
  DELETE,
  UPLOADERROR,
  PROCESSING,
  PROCESSING_FAIL,
  ARCHIVED
}

/**
 * 判斷素材是否已封存。
 * @param creative 帶有 creativeState 的素材
 * @returns 已封存時為 true
 */
export const isArchivedCreative = (creative: {
  creativeState?: CreativeStatus;
}) => creative.creativeState === CreativeStatus.ARCHIVED;

/**
 * 判斷活動綁定的素材是否已封存。
 * @param creative 活動綁定素材，status 為素材狀態
 * @returns 已封存時為 true
 */
export const isArchivedCampaignCreative = (creative: { status: number }) =>
  creative.status === CreativeStatus.ARCHIVED;

/** 素材列表 API `status` 查詢參數的值；API 不帶時等同 ALL。 */
export enum CreativeStatusFilter {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
  ALL = 'all'
}

export enum ApprovalStatus {
  'NEW',
  'PENDING',
  'APPROVED',
  'DENIED',
  'FIXED',
  'INCOMPLETED',
  'DELETED'
}

export enum ADX {
  asiamax = 'AsiaMax',
  doubleclick = 'DoubleClick',
  verizon = 'Verizon',
  facebook = 'Facebook'
}

export enum CREATIVE_LOGO_POSITION {
  LEFT = 'left',
  MIDDLE = 'middle',
  RIGHT = 'right'
}

export enum CREATIVE_DISPLAYSIZE {
  DEFAULT = 'default',
  LARGE = 'large'
}

export type CreativeBasicFields = {
  creativeId?: number;
  advertiserId: number;
  creativeType: CreativeType;
  tenmaxCategory: string;
  name: string;
  enableStartTime?: string | undefined;
  enableEndTime?: string | undefined;
};

export const creativeTypeOrder = [
  CreativeType.ONE_FOR_ALL_DISPLAY,
  CreativeType.NATIVE,
  CreativeType.IMAGE,
  CreativeType.ONE_FOR_ALL_VIDEO,
  CreativeType.VIDEO,
  CreativeType.THIRD_PARTY,
  CreativeType.HTML5,
  CreativeType.EDIMAX,
  CreativeType.PIC_SHORT,
  CreativeType.PIC_LONG,
  CreativeType.PIC_EXTENDED,
  CreativeType.PIC_BROAD,
  CreativeType.COMBO,
  CreativeType.CUSTOM_RECTANGLE,
  CreativeType.CUSTOM_BOTTOM,
  CreativeType.SANDBOX_OUTDOOR,
  CreativeType.FRIDGE_LONG,
  CreativeType.FRIDGE_EXTENDED,
  CreativeType.FRIDGE_BROAD,
  CreativeType.OUTDOOR_LINE_MESSAGE,
  CreativeType.LINE_MESSAGE,
  CreativeType.HWACOM_SHORT,
  CreativeType.HWACOM_LONG,
  CreativeType.HWACOM_EXTENDED,
  CreativeType.HWACOM_BROAD
];

export type CreativeApprovalConfig = {
  bannerTypeId: number;
  outdoorType: number;
  ignoreFields: string[];
  adx: string;
  property: ApprovalConfigProperty;
};

export type ApprovalConfigProperty = {
  selfCheckLink?: string;
};

export const OUTDOOR_DEFAULT_SELFCHECK_DOCUMENT = `https://storage.googleapis.com/${config.cloudStorageConfig.bucket}/adSelfCheck.docx`;

export type CreativeBindingCampaign = {
  id: number;
  l1ObjectId: number;
  name: string;
  state: CampaignBannerMapState;
  startDate: string;
  endDate: string;
  effectiveStatus: string;
  budget: number;
  spent: number;
  enabledCreativeCount: number;
  pmp?: {
    pmpId: number;
    endTime: string;
    status: number;
  };
  originPmp?: {
    pmpId: number;
    endTime: string;
  };
};
